/**
 * Iter XX.222 — My Cricket panel (reworded from XX.221 YourCricketPanel).
 *
 * Reframing: "My Cricket" is the player's cricket RECORD. Provider
 * connection is one of several ingest methods, no longer THE feature.
 *
 * States:
 *   · No performances yet → "Add today's performance" primary CTAs
 *     (chat, scorecard, manual) + provider linkage as SECONDARY
 *     "Also — connect a cricket provider" section.
 *   · ≥1 performance → same but with a compact "matches on record"
 *     summary row up top.
 *
 * Never implies CricHeroes currently imports performance data.
 */
import { useEffect, useState } from "react";
import { Loader2, Unlink, MessageCircle, Camera, PenLine, Plus } from "lucide-react";
import { Link } from "react-router-dom";
import {
  fetchConnection, fetchRecordSummary, disconnectProfile,
} from "../lib/connectCricket";
import ConnectCricketModal from "./ConnectCricketModal";
import AddPerformanceForm from "./AddPerformanceForm";
import { MyCricketCounters } from "./PlusConversionSurfaces";

export { default as YourCricketPanel } from "./YourCricketPanel";   // legacy re-export

export default function MyCricketPanel() {
  const [state, setState] = useState({ loading: true, disabled: false, data: null });
  const [record, setRecord] = useState(null);
  const [providerModalOpen, setProviderModalOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    setState((s) => ({ ...s, loading: true }));
    const conn = await fetchConnection();
    if (!conn) { setState({ loading: false, disabled: true, data: null }); return; }
    const rec = await fetchRecordSummary();
    setRecord(rec);
    setState({ loading: false, disabled: false, data: conn });
  };
  useEffect(() => { reload(); }, []);

  if (state.disabled) return null;
  if (state.loading) {
    return (
      <div className="p-6 border border-neutral-800 rounded-xl"
            data-testid="my-cricket-panel-loading">
        <Loader2 className="animate-spin text-neutral-500" size={16} />
      </div>
    );
  }

  const profile = state.data?.profile;
  const inningsCount = record?.innings_count ?? 0;
  const hasPerformances = inningsCount > 0;
  const isProviderLinked = !!(state.data?.connected && profile);

  return (
    <section
      className="border border-neutral-800 rounded-xl p-6 space-y-4"
      data-testid="my-cricket-panel"
    >
      <div>
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          🏏 My Cricket
        </h3>
        <p className="text-xs text-neutral-500 mt-1">
          {hasPerformances
            ? "The record Gully draws on when we talk about your game."
            : "Gully can remember your performances as you play."
          }
        </p>
      </div>

      {hasPerformances && (
        <div className="bg-neutral-900/50 border border-neutral-800 rounded-lg px-3 py-2 text-xs text-neutral-300 flex items-center justify-between"
              data-testid="my-cricket-panel-summary">
          <span>
            <span className="text-emerald-400 font-medium">{inningsCount}</span>
            {" "}performance{inningsCount === 1 ? "" : "s"} on record
          </span>
          {record?.summary_line && (
            <span className="text-neutral-500 truncate max-w-[60%] hidden sm:block">
              {record.summary_line}
            </span>
          )}
        </div>
      )}

      {/* Iter XX.223 — factual counters. Returns null when the flag is
          off (or the fetch returns null), so production stays silent. */}
      <MyCricketCounters />

      {/* Add-a-performance actions (primary) */}
      <div>
        <div className="text-xs text-neutral-500 mb-2">Add a performance</div>
        <div className="grid grid-cols-3 gap-2" data-testid="my-cricket-panel-add">
          <Link
            to="/chat"
            className="flex flex-col items-center gap-1 bg-neutral-900 hover:bg-neutral-800 border border-neutral-800 rounded-lg px-2 py-3 text-xs text-neutral-200"
            data-testid="my-cricket-panel-add-via-chat"
          >
            <MessageCircle size={16} className="text-emerald-400" />
            Talk to Gully
          </Link>
          <Link
            to="/chat"
            className="flex flex-col items-center gap-1 bg-neutral-900 hover:bg-neutral-800 border border-neutral-800 rounded-lg px-2 py-3 text-xs text-neutral-200"
            data-testid="my-cricket-panel-add-via-scorecard"
          >
            <Camera size={16} className="text-emerald-400" />
            Upload scorecard
          </Link>
          <button
            type="button"
            onClick={() => setManualOpen(true)}
            className="flex flex-col items-center gap-1 bg-neutral-900 hover:bg-neutral-800 border border-neutral-800 rounded-lg px-2 py-3 text-xs text-neutral-200"
            data-testid="my-cricket-panel-add-manual"
          >
            <PenLine size={16} className="text-emerald-400" />
            Add manually
          </button>
        </div>
      </div>

      {/* Provider linkage — SECONDARY */}
      <div className="pt-3 border-t border-neutral-800">
        <div className="text-xs text-neutral-500 mb-2 flex items-center gap-1">
          <Plus size={11} /> Also — connect a cricket provider
        </div>
        {isProviderLinked ? (
          <div className="text-xs space-y-2" data-testid="my-cricket-panel-provider-linked">
            <div className="flex items-center justify-between text-neutral-300">
              <span className="capitalize">{profile.provider}</span>
              <span className="text-emerald-400">Profile linked</span>
            </div>
            <div className="text-neutral-500 truncate">
              <a href={profile.profile_url} target="_blank" rel="noreferrer"
                  className="text-emerald-400 hover:text-emerald-300"
                  data-testid="my-cricket-panel-provider-url">
                {profile.profile_url.replace(/^https?:\/\//, "")}
              </a>
            </div>
            <div className="text-neutral-500">
              Automatic sync not available yet — upload scorecards or talk to Gully to build your record.
            </div>
            <div className="pt-1 flex gap-3">
              <button
                onClick={() => setProviderModalOpen(true)}
                className="text-xs text-neutral-400 hover:text-white"
                data-testid="my-cricket-panel-provider-change"
              >
                Change
              </button>
              <button
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  try {
                    await disconnectProfile(profile.profile_id);
                    await reload();
                  } finally { setBusy(false); }
                }}
                className="text-xs text-neutral-400 hover:text-red-400 flex items-center gap-1 disabled:opacity-50"
                data-testid="my-cricket-panel-provider-disconnect"
              >
                <Unlink size={11} /> Disconnect
              </button>
            </div>
          </div>
        ) : (
          <div className="text-xs text-neutral-400 space-y-2"
                data-testid="my-cricket-panel-provider-empty">
            <p>
              CricHeroes profile link is available (no auto-sync yet).
              Play-Cricket / PlayHQ / CricClubs coming later.
            </p>
            <button
              onClick={() => setProviderModalOpen(true)}
              className="text-xs text-emerald-400 hover:text-emerald-300"
              data-testid="my-cricket-panel-provider-link-cta"
            >
              Link a cricket profile →
            </button>
          </div>
        )}
      </div>

      <ConnectCricketModal
        open={providerModalOpen}
        onClose={() => setProviderModalOpen(false)}
        onSuccess={reload}
      />
      <AddPerformanceForm
        open={manualOpen}
        onClose={() => setManualOpen(false)}
        onSaved={reload}
      />
    </section>
  );
}
